import Record from "./record";
import PublicForm from "./public-form";
import PublicApiCommon from "./public-api-common";
import Field from "./field";
import Entity from "./entity";
import EmailTemplate from "./email-template";
import Email from "./email";
import Documents from "./documents";
import { clone } from "../utils";
import IRecordResponse from "../interface/record-response";
import IPublicFormTokenData from "../interface/public-form-token-data";
import IPublicFormField from "../interface/public-form-field";
import IPublicFormData from "../interface/public-form-data";
import IPublicForm from "../interface/public-form";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IEmailTemplate from "../interface/email-template";
import IDocument from "../interface/document";
import IApiTokenPublicForm from "../interface/api-token-public-form";
import PublicFormType from "../enum/public-form-type";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";

class PublicApiFormSingleton {

    public DefaultRecordId: number = 0;

    /* Public Methods */

    public async load(token: string): Promise<IPublicFormData> {
        const data: IPublicFormTokenData = this.decodePublicFormToken(token);
        await PublicApiCommon.login(data.appId, data.instance);
        const form: IPublicForm | null = PublicForm.get(data.formId);
        if (form) {
            const base: IDocument = await this.getFormBaseDocument(form, data.recordId);
            let record: IDocument | null = null;
            if (form.type === PublicFormType.Update && data.recordId !== this.DefaultRecordId) {
                const response: IRecordResponse = await Record.init(form.entityId, data.recordId);
                Record.clear();
                if (response.record !== null) {
                    record = response.record;
                } else {
                    throw ErrorCode.NoDocument;
                }
            }
            return { form, record, base }
        } else {
            throw ErrorCode.NoPublicForm;
        }
    }

    public async submit(formId: number, model: IDocument): Promise<IDocument> {
        const form: IPublicForm | null = PublicForm.get(formId);
        let doc: IDocument = clone(model);
        if (form) {
            /* Check autocoplete values */
            form.fields.forEach((f: IPublicFormField) => {
                const field: IField | null = Field.get(f.fieldId);
                if (field) {
                    if (f.autocomplete) {
                        if (field.uiType === FieldType.linked) {
                            doc[`c${field.linkedRelation[0]}`] = f.autocomplete;
                        } else {
                            doc[`c${field.id}`] = f.autocomplete;
                        }
                    } else if (f.now) {
                        doc[`c${field.id}`] = (new Date()).toISOString();
                    }
                }
            });
            /* Save result to database */
            doc = await Documents.prepare(form.entityId, doc);
            let result: IDocument;
            if (form.type === PublicFormType.Create) {
                result = await Documents.create(form.entityId, doc);
            } else {
                result = await Documents.replace(form.entityId, doc);
            }
            /* Send notifications */
            for (let i = 0; i < form.notifications.length; i++) {
                const tpl: IEmailTemplate | null = await EmailTemplate.load(form.notifications[i]);
                if (tpl) {
                    await Email.send(tpl, [result], false);
                }
            }
            return result;
        } else {
            throw ErrorCode.NoPublicForm;
        }
    }

    /* Private Methods */

    private async getFormBaseDocument(form: IPublicForm, recipientId: number = 0): Promise<IDocument> {
        const entity: IEntity | null = Entity.get(form.entityId);
        if (entity) {
            const doc: IDocument = Documents.getEmpty(entity);
            form.fields.forEach((f: IPublicFormField) => {
                if (f.autocomplete !== null && (f.autocomplete as any) !== false) {
                    const field: IField | null = Field.get(f.fieldId);
                    if (field) {
                        if (field.uiType === FieldType.linked) {
                            doc[`c${field.linkedRelation}`] = f.autocomplete;
                        } else {
                            doc[`c${field.id}`] = f.autocomplete;
                        }
                    }
                } else if (f.recipient && recipientId && form.type !== PublicFormType.Update) {
                    const field: IField | null = Field.get(f.fieldId);
                    if (field && field.uiType === FieldType.linked) {
                        doc[`c${field.linkedRelation}`] = [recipientId as number];
                    }
                }
            });
            await Documents.loadRelatedDocs(entity, [doc]);
            Documents.setLinkedFieldValues(entity, [doc]);
            return doc;
        }
        return {};
    }

    private decodePublicFormToken(token: string): IPublicFormTokenData {
        try {
            const data: IApiTokenPublicForm = JSON.parse(atob(token));
            return {
                appId: atob(data.a),
                formId: Number(atob(data.f)),
                instance: atob(data.i),
                recordId: Number(atob(data.r))
            } as IPublicFormTokenData;
        } catch (e) {
            throw ErrorCode.InvalidToken;
        }
    }

}

const PublicApiForm = new PublicApiFormSingleton();

export default PublicApiForm;